import type { Cart, Site } from '@chariot/contract';

/**
 * What a scan meant, and how to say it.
 *
 * Kept out of the component and free of any I/O so the wording can be tested. Every failure
 * mode is a distinct case on purpose: an operator standing in a warehouse can only act if
 * told *which* problem they have — a code that is not in the catalogue, a label from a newer
 * version of the app, and an unreadable label call for three different reactions.
 */
export type ScanOutcome =
  | { kind: 'round_opened'; site: Site }
  | {
      kind: 'cart_recorded';
      cart: Cart;
      /** Site the cart was recorded at before this scan, when that differs from here. */
      movedFrom: string | null;
      /** True when the cart had never been scanned anywhere before. */
      firstPlacement: boolean;
      /** True when the cart was under a missing report that this scan has just lifted. */
      clearedMissing: boolean;
    }
  | { kind: 'cart_duplicate'; cart: Cart }
  | { kind: 'cart_written_off'; cart: Cart }
  | { kind: 'no_round'; code: string }
  | { kind: 'other_site'; site: Site }
  /** A site QR was scanned. Recognised, but sites are picked from the list. */
  | { kind: 'site_label'; code: string; name: string }
  | { kind: 'unknown_cart'; code: string }
  | { kind: 'unknown_site'; code: string }
  | { kind: 'unknown_version'; detail: string }
  | { kind: 'legacy_label'; detail: string }
  | { kind: 'malformed'; detail: string };

export type Tone = 'ok' | 'warn' | 'danger' | 'neutral';
export interface ScanMessage {
  tone: Tone;
  text: string;
}

/** `resolveSite` turns a site code into its name; the raw code means nothing to an operator. */
export function describeScan(o: ScanOutcome, resolveSite: (code: string) => string): ScanMessage {
  switch (o.kind) {
    case 'round_opened':
      return { tone: 'ok', text: `Site sous contrôle : ${o.site.name}` };

    case 'cart_recorded':
      // A move and a first placement are both successes, but they are not the same fact,
      // and "confirmé" would be a lie about a cart nobody had ever located.
      if (o.movedFrom) {
        return {
          tone: 'ok',
          text: `${o.cart.name} — déplacé depuis ${resolveSite(o.movedFrom)}${
            o.clearedMissing ? ', signalement levé' : ''
          }`,
        };
      }
      if (o.firstPlacement) {
        return { tone: 'ok', text: `${o.cart.name} — localisé pour la première fois` };
      }
      // Worth its own wording: this is the cart somebody has been looking for, and
      // "confirmé" would not tell them the search is over.
      if (o.clearedMissing) {
        return { tone: 'ok', text: `${o.cart.name} — retrouvé, signalement levé` };
      }
      return { tone: 'ok', text: `${o.cart.name} — confirmé` };

    case 'cart_duplicate':
      return { tone: 'neutral', text: `${o.cart.name} — déjà compté sur cette tournée` };

    case 'cart_written_off':
      return {
        tone: 'warn',
        text: `${o.cart.name} était déclaré ${o.cart.status === 'lost' ? 'perdu' : 'retiré'} — enregistré quand même`,
      };

    case 'no_round':
      return {
        tone: 'warn',
        text: `Aucune tournée en cours — démarrez-en une, ou enregistrez ce chariot seul (${o.code} ignoré)`,
      };

    case 'other_site':
      return {
        tone: 'warn',
        text: `Tournée en cours sur ${o.site.name}. Clôturez-la avant d'en ouvrir une autre.`,
      };

    case 'site_label':
      return {
        tone: 'neutral',
        text: `${o.name} est un site — choisissez-le dans la liste, puis scannez les chariots.`,
      };

    case 'unknown_cart':
      return { tone: 'danger', text: `Chariot inconnu : ${o.code}` };

    case 'unknown_site':
      return { tone: 'danger', text: `Site inconnu : ${o.code}` };

    case 'unknown_version':
      return {
        tone: 'danger',
        text: `Étiquette ${o.detail} plus récente que l'application — mettez l'application à jour`,
      };

    case 'legacy_label':
      return { tone: 'danger', text: `Étiquette de l'ancien prototype (${o.detail}) — à réimprimer` };

    case 'malformed':
      return { tone: 'danger', text: `Code non reconnu${o.detail ? ` : ${o.detail}` : ''}` };
  }
}
